import React from 'react'
import { useLocal } from 'Source/App/Storage/Local';
import { colorShade, isColorDark, colorParse } from 'Common/Utils'; 
import { localkey } from 'Source/App/Main/local-config';

const getMath = (number) => number / Math.abs(number)
const singleDecimal = (number) => parseFloat(number.toFixed(1))
const concaveFactory = (shadeA, shadeB, blur, distancePercent = 1) => (
    `inset ${singleDecimal(shadeA.x * distancePercent)}px ${singleDecimal(shadeA.y * distancePercent)}px ${singleDecimal(blur * distancePercent)}px ${shadeA.color}, \n\tinset ${singleDecimal(shadeB.x * distancePercent)}px ${singleDecimal(shadeB.y * distancePercent)}px ${singleDecimal(blur  * distancePercent)}px ${shadeB.color}`
)

export const boxShadowFactory = ({distance, blur, intensity, radius, convex, concave, float, hill, pressed, color, lightSource, important, engrave = false, engraveUnchange = false, allowRadius = true, insetDistance = 1, insetColor}) => {
    const colorHSP =  isColorDark(color, true)
    // const intensify = colorHSP <= 70 && colorHSP > 30 ? 15 - (colorHSP/5) : 1 
    distance = !engraveUnchange && engrave && distance > 5 ? 5 : distance
    blur = !engraveUnchange && engrave && blur > 10 ? 10 : blur
    if(engraveUnchange) {
        engrave = engraveUnchange
    }
    if(pressed) {
        engrave = pressed
    }
    const darkShade = {
		color: colorShade(colorHSP < 30 ? '#333333' : color, intensity * -1),
		x: distance * (['bottom-right', 'top-right'].includes(lightSource) ? -1 : 1),
		y: distance * (['bottom-right', 'bottom-left'].includes(lightSource) ? -1 : 1),
	};
	const lightShade = {
		color: colorShade(colorHSP < 30 ? '#555555' : color, color === '#ffffff' ? -1 : intensity),
		x: distance * (['top-left', 'bottom-left'].includes(lightSource) ? -1 : 1),
		y: distance * (['top-left', 'top-right'].includes(lightSource) ? -1 : 1),
    };
    const tabs = '\n\t'
    const shadowEffectOne = float || hill ? `0px 0px 0px ${lightShade.color}, ${darkShade.x * (hill ? 0 : 3)}px ${darkShade.y * (hill ? 0 : 3)}px ${blur * (hill ? 0 : 2)}px rgba(0, 0, 0, 0.3)`
    : `${engrave ? 'inset ' : ''}${lightShade.x}px ${lightShade.y}px ${blur}px ${lightShade.color}, ${tabs}${engrave ? 'inset ' : ''}${darkShade.x}px ${darkShade.y}px ${blur}px ${darkShade.color}` 
    const shadowEffectTwo = convex || float || hill
                    ? concaveFactory({...darkShade, color: lightShade.color}, {...lightShade, color: darkShade.color}, distance)
                    : concave 
                    ?  concaveFactory({...darkShade, color: insetColor ? colorShade(insetColor, intensity * -1) : darkShade.color}, {...lightShade, color: insetColor ? colorShade(insetColor, intensity) : lightShade.color}, blur, insetDistance)
                    : pressed 
                    ? `${getMath(lightShade.x)}px ${getMath(lightShade.y)}px 2px ${lightShade.color}, ${tabs}${getMath(darkShade.x)}px ${getMath(darkShade.y)}px 2px ${darkShade.color}`
                    : `${engrave ? '' : 'inset '}0 0 0 ${lightShade.color}, ${tabs}${engrave ? '' : 'inset '}0 0 0 ${darkShade.color}`
    return `${allowRadius && radius ? `border-radius: ${radius}px;${tabs}` : ''}box-shadow: ${engrave ? shadowEffectTwo : shadowEffectOne},${tabs}${engrave ? shadowEffectOne : shadowEffectTwo} ${important ? '!important' : ''};`
}

export const Stylesheet = () => {
    const { getLocal } = useLocal();
	const { color, textColor, lightSource } = getLocal(localkey.general);
    const someGeneralSettings = { color, lightSource }
    const prefix = '.n'
    const buttonSettings = {...getLocal(localkey.button), ...someGeneralSettings}
    const formSettings = {...getLocal(localkey.form), ...someGeneralSettings}
    const freeSettings = {...getLocal(localkey.freestyle), ...someGeneralSettings}
    const tableSettings = {...getLocal(localkey.table), ...someGeneralSettings}
    const cardSettings = {...getLocal(localkey.card), ...someGeneralSettings}
    const modalSettings = {...getLocal(localkey.modal), ...someGeneralSettings}
    const scrollSettings = {...getLocal(localkey.scroll), ...someGeneralSettings}
    const collapseSettings = {...getLocal(localkey.collapse), ...someGeneralSettings}
    const progressSettings = {...getLocal(localkey.progress), ...someGeneralSettings}
    const navsSettings = {...getLocal(localkey.navs), ...someGeneralSettings}
    const dropdownSettings = {...getLocal(localkey.dropdown), ...someGeneralSettings}
    const colorHSP =  isColorDark(color, true)
    const textColorRGB = colorParse(textColor)
    const textColorDim = `rgba(${textColorRGB.r},${textColorRGB.g},${textColorRGB.b},${isColorDark(textColor) ? '0.80': '0.7'})`
    const formPlaceholder = `rgba(${textColorRGB.r},${textColorRGB.g},${textColorRGB.b},${isColorDark(textColor) ? '0.6': '0.7'})`
    const disabled = `rgba(${textColorRGB.r},${textColorRGB.g},${textColorRGB.b},0.6)`
    const formActiveColor = "#0075ff" 
    const primaryColor = "#6c6ce5"
    const successColor = "#51dabb"
    const warningColor = "#ffd15c"
    const dangerColor = "#ff4c60"
    const intensityColor = colorShade(color, colorHSP > 100 ? -3 : 6)
    const borderColor = colorShade(color, colorHSP > 100 ? -5 : 10)
    return ( 
`*, ::after, ::before {
    box-sizing: border-box;
}
html {
    -webkit-text-size-adjust: 100%;
}
body {
    margin: 0;
    font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: ${textColor};
    text-align: left;
    background-color: ${color}
}
button, input, optgroup, select, textarea {
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
}
a, button {
    cursor: pointer;
}
label {
    display: inline-block;
    margin-bottom: 8px;
}
p {
    margin-top: 0;
    margin-bottom: 15px;
}
h6, h5, h4, h3, h2, h1 {
    font-weight: 500;
    margin-top: 0;
    margin-bottom: 8px;
    line-height: 1.2;
}
h1, .h1 {
  font-size: 2.5rem;
}
h2, .h2 {
  font-size: 2rem;
}
h3, .h3 {
  font-size: 1.75rem;
}
h4, .h4 {
  font-size: 1.5rem;
}
h5, .h5 {
  font-size: 1.25rem;
}
h6, .h6 {
  font-size: 1rem;
}
img {
    vertical-align: middle;
    border-style: none;
}
pre {
    margin-top: 0;
    margin-bottom: 15px;
    overflow: auto;
}
ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: 15px;
}
ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}
${prefix}-bg {
    background-color: ${color} !important;
}
${prefix}-bg-intense {
    background-color: ${intensityColor} !important;
}
${prefix}-border-color {
    border-color: ${borderColor} !important;
}
${prefix}-bordered {
    border: 1px solid ${borderColor} !important;
}
${prefix}-text-color {
    color: ${textColor} !important;
}
${prefix}-text-dim-color {
    color: ${textColorDim} !important;
}
${prefix}-text-dim-color${prefix}-text-hover:hover,
${prefix}-text-dim-color${prefix}-text-hover.active {
    color: ${textColor} !important;
}
::-webkit-scrollbar {
    -webkit-overflow-scrolling: auto;
    width: 20px;
}
::-webkit-scrollbar-track {
    background-color: ${colorShade(color, -2)};
    ${boxShadowFactory({...scrollSettings, color: colorShade(color, -2), engrave: true, allowRadius: false})}
}
::-webkit-scrollbar-track-piece:start {
    background-color: ${colorShade(color, -3)};
    ${boxShadowFactory({...scrollSettings, color: colorShade(color, -3), engrave: true, allowRadius: false})}
}
::-webkit-scrollbar-thumb {
    background-color: ${color};
    ${boxShadowFactory({...scrollSettings, hill: true})}
}
${prefix}-btn {
    display: inline-block;
    user-select: none;
    text-decoration: none !important;
    color: ${textColor}; 
    background-color: ${color};
    outline: 0 !important; 
    border: ${buttonSettings.convex ? '0' : `1px solid ${borderColor}`}; 
    padding: 5px 10px;
    transition: box-shadow 300ms ease-in-out;
    ${boxShadowFactory(buttonSettings)}
}
input${prefix}-btn {
    -webkit-appearance: none;
    appearance: none;
}
${prefix}-btn-xs {
    padding: 0 6px;
    font-size: 13px;
}
${prefix}-btn-sm {
    padding: 3px 8px;
    font-size: 13px;
}
${prefix}-btn-lg {
    padding: 10px 15px;
    font-size: 16px;
    font-weight: 500;
}
a${prefix}-btn:hover {
    color: ${textColor};
}
${prefix}-btn:focus, ${prefix}-btn.active {
    background-color: ${colorShade(color, -1)};
    ${boxShadowFactory({...buttonSettings, convex: false, pressed: true, allowRadius: false})}
}
${prefix}-btn:disabled, ${prefix}-btn.disabled {
    color: ${disabled};
    border: 1px solid ${borderColor};
    box-shadow: none;
    pointer-events: none;
}
${prefix}-form-field {
    display: block;
    margin-bottom: 15px;
}
${prefix}-form-field label {
    margin-bottom: 8px;
}
label${prefix}-form-check {
    display: flex;
    align-items: center;
}
label${prefix}-form-check-inline {
    display: inline-flex;
    align-items: center;
}
label${prefix}-form-check-inline:not(:last-child) {
    margin-right: 10px;
}
label${prefix}-form-check-inline > ${prefix}-form-control,
label${prefix}-form-check > ${prefix}-form-control {
    margin-right: 10px;
}
${prefix}-form-control {
    display: block;
    width: 100%;  
    color: ${textColor}; 
    background-color: ${color};
    outline: 0 !important;
    -webkit-appearance: none;
    appearance: none;
    border: ${formSettings.engrave ? 0 : formSettings.border}px solid ${color}; 
    padding: ${(formSettings.engrave ? formSettings.border : 0) + 5 }px ${(formSettings.engrave ? formSettings.border : 0) + 15 }px;
    transition: box-shadow 300ms ease-in-out;
    ${boxShadowFactory({...formSettings, distance: singleDecimal(formSettings.distance/2), blur: singleDecimal(formSettings.blur/2), insetDistance: 0.5, engraveUnchange: formSettings.engrave })}
}
${prefix}-form-control:focus, 
${prefix}-form-control.active,
input[type=color]${prefix}-form-control:focus {
    ${boxShadowFactory({...formSettings, allowRadius: false})}
}
${prefix}-form-control:disabled,
${prefix}-form-control[readonly],
${prefix}-switch > input[type=checkbox]:disabled + label:before {
    background-color: ${colorShade(color, -10)};
}
${prefix}-form-control::placeholder {
    color: ${formPlaceholder};
}
${prefix}-invalid-feedback,
${prefix}-valid-feedback {
	display: none;
}
${prefix}-form-invalid ${prefix}-form-control {
	color: ${dangerColor};
    border: ${formSettings.border}px solid ${dangerColor};
}
${prefix}-form-invalid ${prefix}-invalid-feedback {
	color: ${dangerColor} !important;
}
${prefix}-form-valid ${prefix}-form-control {
    border: ${formSettings.border}px solid ${successColor};
}
${prefix}-form-valid ${prefix}-valid-feedback {
	color: ${successColor} !important;
}
${prefix}-form-invalid ${prefix}-invalid-feedback, ${prefix}-error-feedback,
${prefix}-form-valid ${prefix}-valid-feedback {
	display: block; 
    margin-top: 3px;
}
input[type=range]${prefix}-form-control {
    padding: 0;
    border-radius: 50px;
}
input[type=range]${prefix}-form-control::-webkit-slider-thumb { 
    -webkit-appearance: none;
    appearance: none;
    width: 15px;
    height: 15px;
    border-radius: 50px; 
    background: ${color};
    cursor: pointer;
    ${boxShadowFactory({...formSettings, engrave: false, distance: singleDecimal(formSettings.distance/3), blur: singleDecimal(formSettings.blur/3), concave: false, allowRadius: false})}
}
input[type=range]${prefix}-form-control:disabled::-webkit-slider-thumb {
    cursor: default;
}
input[type=range]${prefix}-form-control::-moz-range-thumb {
    width: 15px;
    height: 15px;
    border-radius: 50px; 
    background: ${color};
    cursor: pointer;
    ${boxShadowFactory({...formSettings, engrave: false, distance: singleDecimal(formSettings.distance/3), blur: singleDecimal(formSettings.blur/3), concave: false, allowRadius: false})}
}
input[type=range]${prefix}-form-control:disabled::-moz-range-thumb {
    cursor: default;
}
input[type=checkbox]${prefix}-form-control,
input[type=radio]${prefix}-form-control { 
    display: inline-block; 
    height: 22px;
    width: 22px;
    padding: 0;
    border-radius: 5px;
}
input[type=radio]${prefix}-form-control { 
    border-radius: 50%; 
}
input[type=checkbox]${prefix}-form-control:checked,
input[type=radio]${prefix}-form-control:checked {
    background-color: ${formActiveColor};
    border: ${formSettings.border}px solid ${color}; 
    ${boxShadowFactory({...formSettings, engrave: false, allowRadius: false, insetDistance: formSettings.engrave ? 0.5 : 0.1})}
}
input[type=color]${prefix}-form-control { 
    display: inline-block;
    width: 50px; 
    height: 30px;
    padding: 0;
    ${boxShadowFactory({...formSettings, distance: singleDecimal(formSettings.distance/2), blur: singleDecimal(formSettings.blur/2), allowRadius: false })}
}
input[type=color]${prefix}-form-control::-webkit-color-swatch-wrapper {
    padding: ${formSettings.engrave ? 6 : 4}px; 
}
input[type=color]${prefix}-form-control::-webkit-color-swatch {
    border: 0;
    border-radius: ${singleDecimal(formSettings.radius/2)}px; 
}
${prefix}-switch {
    display: block;
    position: relative; 
}
${prefix}-switch > input[type=checkbox] {
    position: absolute;
    opacity: 0;
}
${prefix}-switch > ${prefix}-switch-label {
    display: flex;
    position: relative;
    align-items: center;
    user-select: none;
}
${prefix}-switch > ${prefix}-switch-label:before,
${prefix}-switch > ${prefix}-switch-label:after {
    content: "";
    display: inline-block;
    height: 22px;
    width: 42px; 
    background-color: ${color};
    border: ${formSettings.engrave ? 1 : formSettings.border}px solid ${color};
    border-radius: 50px; 
}
${prefix}-switch > ${prefix}-switch-label:before {
    margin-right: 10px;
    transition: background-color 100ms ease-in-out;
    ${boxShadowFactory({...formSettings, distance: singleDecimal(formSettings.distance/2), blur: singleDecimal(formSettings.blur/2), insetDistance: 0.5, engraveUnchange: formSettings.engrave, allowRadius: false })}
}
${prefix}-switch > ${prefix}-switch-label:after { 
    position: absolute;
    left: 0;  
    width: 22px;
    transition: left 150ms ease-in-out;
    ${boxShadowFactory({...formSettings, engrave: false, distance: 1, blur: 2, concave: !formSettings.engrave, allowRadius: false})}
}
${prefix}-switch > input[type=checkbox]:checked + label:after {
    transition: left 100ms ease-out;
    left: 22px; 
}
${prefix}-switch > input[type=checkbox]:checked + label:before { 
    background-color: ${formActiveColor}; 
    ${formSettings.engrave ? boxShadowFactory({...formSettings, color: formActiveColor, hill: true, intensity: Math.max(20, formSettings.intensity), distance: singleDecimal(formSettings.distance/2), blur: singleDecimal(formSettings.blur/2), insetDistance: 0.5, engraveUnchange: formSettings.engrave, allowRadius: false }) 
    : boxShadowFactory({...formSettings, insetColor: formActiveColor, distance: singleDecimal(formSettings.distance/2), blur: singleDecimal(formSettings.blur/2), insetDistance: 0.5, engraveUnchange: formSettings.engrave, allowRadius: false })}
}
${prefix}-input-group {
    display: flex;
    ${formSettings.engrave ? '' : boxShadowFactory({...formSettings, concave:false, distance: singleDecimal(formSettings.distance/2), blur: singleDecimal(formSettings.blur/2), insetDistance: 0.5, engrave: false })}
}
${prefix}-input-group-prepend, 
${prefix}-input-group-append { 
    display: flex;
    position: relative;
    background-color: ${color};
    padding: ${formSettings.engrave ? 0 : formSettings.border}px;
}
${prefix}-input-group-prepend {
    padding-right: 0;
    z-index: 1;
}
${prefix}-input-group-append {
    padding-left: 0;
}
${prefix}-input-group > ${prefix}-input-group-prepend:first-child > * { 
    border-radius: 0 !important;
    border-top-left-radius: ${formSettings.engrave ? formSettings.radius : singleDecimal(formSettings.radius/2)}px !important;
    border-bottom-left-radius: ${formSettings.engrave ? formSettings.radius : singleDecimal(formSettings.radius/2)}px !important;
}
${prefix}-input-group > ${prefix}-input-group-append:last-child > * {
    border-radius: 0 !important;
    border-top-right-radius: ${formSettings.engrave ? formSettings.radius : singleDecimal(formSettings.radius/2)}px !important;
    border-bottom-right-radius: ${formSettings.engrave ? formSettings.radius : singleDecimal(formSettings.radius/2)}px !important;
}
${prefix}-input-group > ${prefix}-input-group-prepend:first-child { 
    border-radius: 0 !important;
    border-top-left-radius: ${formSettings.radius}px !important;
    border-bottom-left-radius: ${formSettings.radius}px !important;
}
${prefix}-input-group > ${prefix}-input-group-append:last-child {
    border-radius: 0 !important;
    border-top-right-radius: ${formSettings.radius}px !important;
    border-bottom-right-radius: ${formSettings.radius}px !important;
}
${prefix}-input-group > ${prefix}-input-group-prepend > *,
${prefix}-input-group > ${prefix}-input-group-append > *
{   
    border-radius: 0;
}
${formSettings.engrave 
? `${prefix}-input-group > ${prefix}-input-group-prepend > :not(${prefix}-btn),
${prefix}-input-group > ${prefix}-input-group-append > :not(${prefix}-btn) {
    border: 1px solid ${borderColor};
    border-left-width: 0;
    border-right-width: 0;
}
${prefix}-input-group > ${prefix}-input-group-prepend:first-child > :not(${prefix}-btn) {
    border-left-width: 1px;
}
${prefix}-input-group > ${prefix}-input-group-append:last-child > :not(${prefix}-btn) {
    border-right-width: 1px;
}` 
: `${prefix}-input-group > ${prefix}-input-group-prepend > :not(${prefix}-input-group-text),
${prefix}-input-group > ${prefix}-input-group-append > :not(${prefix}-input-group-text)
{    
    ${boxShadowFactory({...buttonSettings, intensity: formSettings.intensity, distance: singleDecimal(formSettings.border/2), blur: singleDecimal(formSettings.border/2), allowRadius: false})}
}
${prefix}-input-group ${prefix}-btn:focus,
${prefix}-input-group ${prefix}-btn.active { 
    ${boxShadowFactory({...buttonSettings, convex: false, engrave: true, important: true, intensity: formSettings.intensity, distance: singleDecimal(formSettings.border/2), blur: singleDecimal(formSettings.border/2), allowRadius: false})}
}`}
${prefix}-input-group ${prefix}-btn {
    border: 0 !important;
}
${prefix}-input-group-text 
{  
    display: flex; 
    align-items: center;
    padding: 5px 10px;
}
${prefix}-input-group > ${prefix}-form-control { 
    border-radius: 0;
    ${formSettings.engrave ? '' : boxShadowFactory({...formSettings, concave:false, distance: singleDecimal(formSettings.distance/2), blur: singleDecimal(formSettings.blur/2), insetDistance: 0.5, engrave: true, allowRadius: false })}
}
${prefix}-input-group > ${prefix}-form-control:first-child {
    border-top-left-radius: ${formSettings.radius}px;
    border-bottom-left-radius: ${formSettings.radius}px;
    margin-right: 1px;
}
${prefix}-input-group > ${prefix}-form-control:last-child {
    border-top-right-radius: ${formSettings.radius}px;
    border-bottom-right-radius: ${formSettings.radius}px;
    margin-left: 1px;
}
${prefix}-input-group > ${prefix}-form-control:not(:first-child):not(:last-child) { 
    margin-left: 1px;
    margin-right: 1px;
}
${prefix}-input-group > ${prefix}-form-control[type=color] {
    width: 80px;
    height: auto;
}
${formSettings.engrave ? '' : `${prefix}-input-group > ${prefix}-form-control:focus {
    ${boxShadowFactory({...formSettings, concave: false, engraveUnchange: true, allowRadius: false})}
}`}
${prefix}-input-group > ${prefix}-form-control + ${prefix}-form-control {
    margin-left: -${formSettings.border}px
}
${prefix}-table {
    border-collapse: collapse;
    width: 100%;
    background-color: ${color};
    margin-bottom: 15px;
    ${boxShadowFactory({...tableSettings, allowRadius: false})}
}
${prefix}-table th {
    font-weight: 500;
    background-color: ${color}; 
}
${prefix}-table:not(${prefix}-table-flated) th{
    border: ${tableSettings.border}px solid ${color};
}
${prefix}-table:not(${prefix}-table-flated) th:not(:last-child) {
    border-right-width: 0;
}
${prefix}-table:not(${prefix}-table-flated) th:not(:first-child) { 
    border-left-width: 0;
}
${prefix}-table th,
${prefix}-table td {
    font-size: 14px;
    line-height: 2;
    color: ${textColor};
    padding: 8px 10px; 
}
${prefix}-table${prefix}-table-flated th,
${prefix}-table${prefix}-table-flated td { 
    padding: ${tableSettings.border + 5}px ${tableSettings.border + 10}px;
}
${prefix}-table${prefix}-table-flated tbody > tr:not(:last-child) {
    border-bottom: 1px solid ${borderColor};
}
${prefix}-table${prefix}-table-flated thead > tr {
    border-bottom: 2px solid ${borderColor};
}
${prefix}-table:not(${prefix}-table-flated):not(${prefix}-table-bordered) > tbody tr,
${prefix}-table${prefix}-table-bordered:not(${prefix}-table-flated) td { 
    border: ${tableSettings.border}px solid ${color};
    ${boxShadowFactory({...tableSettings, engrave: true, allowRadius: false})}
}
${prefix}-table${prefix}-table-hover:not(${prefix}-table-bordered) > tbody tr:hover,
${prefix}-table${prefix}-table-bordered${prefix}-table-hover tr:hover td,
${prefix}-table${prefix}-table-flated thead,
${prefix}-table-bordered:not(${prefix}-table) thead {
    background-color: ${intensityColor}; 
}
${prefix}-table-bordered:not(${prefix}-table) {
    margin-bottom: 0;
    border-spacing: 0;
}
${prefix}-table-bordered:not(${prefix}-table) td,
${prefix}-table-bordered:not(${prefix}-table) th { 
    border: 1px solid ${borderColor};
}
${prefix}-table-bordered:not(${prefix}-table) td:first-child,
${prefix}-table-bordered:not(${prefix}-table) th:first-child {
    border-left-width: 0;
}
${prefix}-table-bordered:not(${prefix}-table) td:last-child,
${prefix}-table-bordered:not(${prefix}-table) th:last-child {
    border-right-width: 0;
}
${prefix}-table-bordered:not(${prefix}-table) tbody tr:last-child > td {
    border-bottom-width: 0;
}
${prefix}-table-bordered:not(${prefix}-table) thead tr:first-child > th {
    border-top-width: 0;
}
${prefix}-card {
    display: block;
    position: relative; 
    ${boxShadowFactory(cardSettings)}
}
${prefix}-modal-header,
${prefix}-modal-body,
${prefix}-modal-footer,
${prefix}-card-header,
${prefix}-card-body,
${prefix}-card-footer {
    display: block;
    background-color: ${color};
    padding: 10px 15px;
}
${prefix}-card-header,
${prefix}-card-footer { 
    font-size: 15px;
    line-height: 24px;
}
${prefix}-modal-content > :first-child,
${prefix}-card > :first-child { 
    border-top-left-radius: ${cardSettings.radius}px;
    border-top-right-radius: ${cardSettings.radius}px;
}
${prefix}-card > :not(${prefix}-card-img):first-child {
    border-bottom: 1px solid ${borderColor};
}
${prefix}-modal-content > :last-child,
${prefix}-card > :last-child { 
    border-bottom-left-radius: ${cardSettings.radius}px;
    border-bottom-right-radius: ${cardSettings.radius}px;
}
${prefix}-modal-content > ${prefix}-modal-footer,
${prefix}-card > ${prefix}-card-footer {
    border-top: 1px solid ${borderColor};
}
${prefix}-card-img { 
    width: 100%;
    border: ${cardSettings.border}px solid ${color};
}
${prefix}-card-img + ${prefix}-card-body {
    border: 0;
    box-shadow: none;
}
${prefix}-card-text {
    font-size: 14px; 
    line-height: 22px;
    margin: 0;
}
${prefix}-card ${prefix}-table {
    box-shadow: none;
}
${prefix}-modal,
${prefix}-modal:before {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
body${prefix}-modal-shown {
    overflow: hidden !important;
    padding-right: 20px;
}
${prefix}-modal { 
    z-index: 1050;
    display: none; 
    overflow: auto;
    outline: 0;
}
${modalSettings.float ? `${prefix}-modal:not(.for-show):before {
    content: "";
    display: block; 
    background-color: ${modalSettings.float ? colorShade(color, -50) : color};
}
${prefix}-modal.show:before {
    opacity: 0.3;
}`: ''}
${prefix}-modal:before,
${prefix}-modal ${prefix}-modal-content {
    opacity: 0;
    transition: opacity 150ms ease-out 250ms;
}
${prefix}-modal-dialog { 
    position: relative; 
    background-color: ${color};
    padding: ${modalSettings.blur}px;
    opacity: 0; 
    width: auto;
    margin: .5rem;
    transition: box-shadow 250ms ease-out 400ms, opacity 150ms ease-out 650ms;
    ${boxShadowFactory({...modalSettings, distance: 0, blur: 0 })}
}
@media (min-width: 576px) {
    ${prefix}-modal-dialog {
        max-width: 500px;
        margin: 3rem auto;
    }
}
${prefix}-modal-sm {
    max-width: 300px;
    margin: 1rem auto;
}
@media (min-width: 992px) {
    ${prefix}-modal-lg {
        max-width: 800px;
    }
}
@media (min-width: 1200px)  {
    ${prefix}-modal-xl {
        max-width: 1100px;
    }
}
${prefix}-modal ${prefix}-modal-content {
    position: relative;
} 
${prefix}-modal-header {
    display: flex;
    justify-content: space-between; 
    align-items: center;
}
${prefix}-modal-header > ${prefix}-modal-title {
    line-height: 28px;
    margin: 0;
}
${prefix}-modal-footer {
    display: flex;
    justify-content: flex-end;
}
${prefix}-modal-footer > :not(:first-child) {
    margin-left: 10px;
}
${prefix}-modal.show {
    display: block;
}
${prefix}-modal.show > ${prefix}-modal-dialog {
    ${boxShadowFactory({...modalSettings, allowRadius: false})}
    opacity: 1;
    transition: box-shadow 250ms ease-in 150ms, opacity 150ms ease-in;
}
${prefix}-modal.show ${prefix}-modal-content {
    opacity: 1;
    transition: opacity 150ms ease-in 400ms;
}
${prefix}-modal ${prefix}-modal-content > ${prefix}-card {
    box-shadow: none;
}
${prefix}-modal.transition:not(.show) ${prefix}-modal-content [class*="${prefix.substr(1)}-"] {
    transition: box-shadow 300ms ease-in;
    ${boxShadowFactory({...modalSettings, distance: 0, blur: 0, allowRadius: false})}
}
${prefix}-modal.show.transition ${prefix}-modal-content [class*="${prefix.substr(1)}-"] {
    transition: box-shadow 300ms ease-in 450ms; 
}
[data-collapse] {
    cursor: pointer;
}
${prefix}-collapse {
    display: none;
    overflow: hidden;
    transition: height 300ms ease-in 300ms, box-shadow 300ms ease-in;
    ${boxShadowFactory({...collapseSettings, distance: 1})}
}
${prefix}-collapse.show {
    display: block;
    transition: height 300ms ease-in, box-shadow 300ms ease-in 300ms;
    ${boxShadowFactory({...collapseSettings, engraveUnchange: collapseSettings.engrave})}  
}
${prefix}-collapse[data-shadow=false] {  
    box-shadow: none;
}
${prefix}-collapse:not([data-shadow=false]) > ${prefix}-collapse-content { 
    padding: ${collapseSettings.engrave ? Math.max(collapseSettings.blur * 2, 10) : '10px 15'}px;
}
${prefix}-collapse > ${prefix}-collapse-content {
    display: block;
    opacity: 0;
    transition: opacity 150ms ease-in 200ms;
}
${prefix}-collapse.show > ${prefix}-collapse-content {
    opacity: 1;
    transition: opacity 150ms ease-in 300ms;
}
${prefix}-collapse.transition:not(.show) > ${prefix}-collapse-content [class*="${prefix.substr(1)}-"] {
    transition: box-shadow 300ms ease-in;
    ${boxShadowFactory({...collapseSettings, distance: 0, blur: 0, allowRadius: false})}
}
${prefix}-collapse.transition > ${prefix}-collapse-content [class*="${prefix.substr(1)}-"] {
    transition: box-shadow 300ms ease-in 300ms; 
}
${prefix}-progress {
    ${progressSettings.concave ? `border: ${progressSettings.border}px solid ${color};` : ''}
    ${boxShadowFactory(progressSettings)}
}
${prefix}-progress,
${prefix}-progress-bar {
    display: block;
    width: 100%; 
    border-radius: 50px;
}
${prefix}-progress-bar { 
    position: relative;
    height: 10px; 
    background-color: ${primaryColor};
    ${boxShadowFactory({...progressSettings, color: primaryColor, distance: 1, blur: 2, engrave: false, concave: false, hill: true, allowRadius: false})}
}
${prefix}-progress-bar${prefix}-success {
    background-color: ${successColor};
    ${boxShadowFactory({...progressSettings, color: successColor, distance: 1, blur: 2, engrave: false, concave: false, hill: true, allowRadius: false})}
}
${prefix}-progress-bar${prefix}-warning {
    background-color: ${warningColor};
    ${boxShadowFactory({...progressSettings, color: warningColor, distance: 1, blur: 2, engrave: false, concave: false, hill: true, allowRadius: false})}
}
${prefix}-progress-bar${prefix}-danger {
    background-color: ${dangerColor};
    ${boxShadowFactory({...progressSettings, color: dangerColor, distance: 1, blur: 2, engrave: false, concave: false, hill: true, allowRadius: false})}
}
${prefix}-progress-bar[data-percentage]:before {
    content: attr(data-percentage);
    transform: translateX(50%);
    background-color: ${color};
    padding: 5px 10px;
    border-radius: 5px;
    border: 1px solid ${borderColor};
    ${boxShadowFactory({...someGeneralSettings, distance: 7, blur: 14, intensity: 40})}
}
${prefix}-progress-bar[data-percentage]:after {
    content: "";
    top: -18px;
    border: 7px solid transparent;
    border-top-color: ${color};
    transform: translateX(50%);
}
${prefix}-progress-bar:after,
${prefix}-progress-bar:before {
    position: absolute;
    top: -50px;
    right: 0;
}
${prefix}-nav {
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
}
${prefix}-nav ${prefix}-nav-link {
    display: block;
    color: ${textColor}; 
    padding: 8px 13px;
    cursor: pointer;
    text-decoration: none;
    transition: box-shadow 300ms ease-in-out;
    ${boxShadowFactory({...navsSettings, distance: 0, blur: 0})}
}
${prefix}-nav ${prefix}-nav-link.disabled {
    color: ${disabled};
    pointer-events: none;
    user-select: none;
}
${prefix}-nav${prefix}-nav-column {
    flex-direction: column;
}
${prefix}-nav${prefix}-nav-flat ${prefix}-nav-link:not(.disabled) {
    border: 1px solid ${borderColor};
    ${boxShadowFactory(navsSettings)}
}
${prefix}-nav${prefix}-nav-flat:not(${prefix}-nav-column) ${prefix}-nav-link:not(:last-child) {
    margin-right: ${navsSettings.blur}px;
}
${prefix}-nav${prefix}-nav-flat${prefix}-nav-column ${prefix}-nav-link:not(:last-child) {
    margin-bottom: ${navsSettings.blur}px;
}
${prefix}-nav ${prefix}-nav-link:not(.disabled):focus, 
${prefix}-nav:not(${prefix}-no-hover) ${prefix}-nav-link:not(.disabled):hover,
${prefix}-nav ${prefix}-nav-link:not(.disabled).active {
    background-color: ${colorShade(color, -1)};
    ${boxShadowFactory({...navsSettings, engrave: true})}
}
${prefix}-dropdown {
    position: relative;
    display: inline-block;
}
${prefix}-dropdown.show:not(.for-show):before {
    content: "";
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1020;
}
${prefix}-dropdown > ${prefix}-dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 150px;
    background-color: ${color};
    margin: ${dropdownSettings.blur}px 0 0;
    z-index: 1030; 
    opacity: 0;
    ${boxShadowFactory({...dropdownSettings, distance: 0, blur: 0})}
    transition: opacity 150ms ease-out 300ms, box-shadow 300ms ease-out;
}
${prefix}-dropdown.show > ${prefix}-dropdown-menu {
    display: block;
    opacity: 1;
    ${boxShadowFactory(dropdownSettings)}
    transition: opacity 150ms ease-in, box-shadow 300ms ease-in 150ms;
}
${prefix}-dropdown-menu > ${prefix}-dropdown-content {
    opacity: 0;
    padding: ${dropdownSettings.blur}px;
    transition: opacity 150ms ease-out 150ms;
}
.show > ${prefix}-dropdown-menu > ${prefix}-dropdown-content {
    opacity: 1; 
    transition: opacity 150ms ease-in 450ms;
}
${prefix}-dropdown-content > ${prefix}-dropdown {
    display: block;
}
${prefix}-dropdown-content ${prefix}-dropdown-item {
    display: block;
    width: 100%;
    padding: 10px 15px;
    color: ${textColorDim};
    text-decoration: none;
    white-space: nowrap;
    ${boxShadowFactory({...dropdownSettings, distance: 0, blur: 0, float: false})}
    transition: box-shadow 300ms ease-in-out;
}
.show > ${prefix}-dropdown-item,
${prefix}-dropdown-item:hover {
    color: ${textColor};
    ${boxShadowFactory({...dropdownSettings, engrave: true, float: false, allowRadius: false})}
}
${prefix}-dropdown.transition:not(.show) ${prefix}-dropdown-content [class*="${prefix.substr(1)}-"]:not(${prefix}-dropdown-menu):not(${prefix}-dropdown-item) {
    transition: box-shadow 150ms ease-in;
    ${boxShadowFactory({...dropdownSettings, distance: 0, blur: 0, allowRadius: false})}
}
${prefix}-dropdown.transition ${prefix}-dropdown-content [class*="${prefix.substr(1)}-"]:not(${prefix}-dropdown-menu):not(${prefix}-dropdown-item) {
    transition: box-shadow 200ms ease-in 550ms; 
}
@media screen and (min-width: 401px) {
    ${prefix}-dropdown-content > ${prefix}-dropdown.show:before {
        content: none;
    }
    ${prefix}-dropup > ${prefix}-dropdown-menu {
        top: unset;
        bottom: 100%;
        margin: 0 0 ${dropdownSettings.blur}px;
    }
    ${prefix}-dropend > ${prefix}-dropdown-menu {
        left: unset;
        right: 0;
    }
    ${prefix}-dropup-stack > ${prefix}-dropdown-menu {
        margin: 0;
        max-height: 100vh;
        overflow-y: auto;
    }
    ${prefix}-dropend-stack > ${prefix}-dropdown-menu {
        max-width: 100vw;
        overflow-x: auto;
    }
    ${prefix}-dropdown-content > ${prefix}-dropdown > ${prefix}-dropdown-menu {
        top: 0;
        left: 100%;
        margin: 0 ${dropdownSettings.blur * (dropdownSettings.float ? 1.5 : 2)}px 0;
    }
    ${prefix}-dropdown-content > ${prefix}-dropend > ${prefix}-dropdown-menu {
        left: unset;
        right: 100%;
    }
    ${prefix}-dropdown-content > ${prefix}-dropup > ${prefix}-dropdown-menu {
        top: unset;
        bottom: 0;
    }
}
@media screen and (max-width: 400px) {
    ${prefix}-dropdown > ${prefix}-dropdown-menu {
        position: fixed;
        top: unset !important;
        left: 15px !important;
        right: 15px !important;
        bottom: 15px !important;
        max-height: 50vh;
        // overflow-y: auto;
    }
}
${prefix}-no-shadow,
${prefix}-hover-no-shadow:hover { 
    ${boxShadowFactory({...freeSettings, distance: 0, blur: 0, allowRadius: false})}
}
${prefix}-flat-low,
${prefix}-hover-flat-low:hover {
    ${boxShadowFactory({...freeSettings, allowRadius: false, distance: freeSettings.distance/2, blur: freeSettings.blur/2})}
}
${prefix}-concave-low,
${prefix}-hover-concave-low:hover { 
    ${boxShadowFactory({...freeSettings, allowRadius: false, distance: freeSettings.distance/2, blur: freeSettings.blur/2, concave: true})}
}
${prefix}-convex-low,
${prefix}-hover-convex-low:hover { 
    ${boxShadowFactory({...freeSettings, allowRadius: false, distance: freeSettings.distance/2, blur: freeSettings.blur/2, convex: true})}
}
${prefix}-engrave-low,
${prefix}-hover-engrave-low:hover {
    ${boxShadowFactory({...freeSettings, allowRadius: false, distance: freeSettings.distance/2, blur: freeSettings.blur/2, engraveUnchange: true})}
}
${prefix}-float-low,
${prefix}-hover-float-low:hover {
    ${boxShadowFactory({...freeSettings, allowRadius: false, distance: freeSettings.distance/2, blur: freeSettings.blur/2, float: true})}
}
${prefix}-flat,
${prefix}-hover-flat:hover {
    ${boxShadowFactory({...freeSettings, allowRadius: false})}
}
${prefix}-concave,
${prefix}-hover-concave:hover { 
    ${boxShadowFactory({...freeSettings, allowRadius: false, concave: true})}
}
${prefix}-convex,
${prefix}-hover-convex:hover { 
    ${boxShadowFactory({...freeSettings, allowRadius: false, convex: true})}
}
${prefix}-engrave,
${prefix}-hover-engrave:hover {
    ${boxShadowFactory({...freeSettings, allowRadius: false, engraveUnchange: true})}
}
${prefix}-float,
${prefix}-hover-float:hover {
    ${boxShadowFactory({...freeSettings, allowRadius: false, float: true})}
}
${prefix}-concave-low,
${prefix}-hover-concave-low,
${prefix}-concave,
${prefix}-hover-concave {
    border: ${freeSettings.border}px solid ${color};
}
${prefix}-flat-low,
${prefix}-concave-low,
${prefix}-convex-low,
${prefix}-engrave-low,
${prefix}-float-low,
${prefix}-flat,
${prefix}-concave,
${prefix}-convex,
${prefix}-engrave,
${prefix}-float {
    background-color: ${color};
}
${prefix}-hover-no-shadow,
${prefix}-hover-flat-low,
${prefix}-hover-concave-low,
${prefix}-hover-convex-low,
${prefix}-hover-engrave-low,
${prefix}-hover-float-low,
${prefix}-hover-flat,
${prefix}-hover-concave,
${prefix}-hover-convex,
${prefix}-hover-engrave,
${prefix}-hover-float {
    transition: box-shadow 300ms ease-in-out;
}`
    )
}